import React, { useEffect, useState } from "react";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import * as yup from "yup";
import { useFormik } from "formik";
import api from "../../../config/URL";
import { toast } from "react-toastify";
import { BiEditAlt } from "react-icons/bi";
import fetchAllCentersWithIds from "../../List/CenterList";
import { MultiSelect } from "react-multi-select-component";
import { FaRegCopy } from "react-icons/fa";

function CopyCenterPackages({ packageIds, onSuccess }) {
  const [show, setShow] = useState(false);
  const [loadIndicator, setLoadIndicator] = useState(false);
  const [isModified, setIsModified] = useState(false);
  const [selectedCenters, setSelectedCenters] = useState([]);
  const [centerData, setCenterData] = useState([]);

  const centerOptions = centerData?.map((center) => ({
    label: center.centerNames,
    value: center.id,
  }));

  const handleClose = () => {
    setShow(false);
    setIsModified(false);
    setSelectedCenters([]);
    formik.resetForm();
  };

  const handleShow = () => {
    setShow(true);
    setIsModified(false);
    formik.setFieldValue("packageIds", packageIds);
  };

  const validationSchema = yup.object().shape({
    centerIds: yup
      .array()
      .min(1, "*At least one center must be selected")
      .required("*Center selection is required"),
    packageIds: yup
      .array()
      .min(1, "*At least one package must be selected")
      .required("*Package selection is required"),
  });

  const formik = useFormik({
    initialValues: {
      centerIds: [],
      packageIds: packageIds || [],
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      setLoadIndicator(true);
      try {
        const response = await api.post(`/reusableCenterPackage`, values, {
          headers: {
            "Content-Type": "application/json",
          },
        });
        if (response.status === 200) {
          toast.success(response.data.message);
          onSuccess();
          handleClose();
        } else {
          toast.error(response.data.message);
        }
      } catch (error) {
        if (error.response?.status === 409) {
          toast.warning(error?.response?.data?.message);
        } else {
          toast.error(error.response?.data?.message || "An error occurred");
        }
      } finally {
        setLoadIndicator(false);
      }
    },
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
  });

  const fetchData = async () => {
    try {
      const centerData = await fetchAllCentersWithIds();
      setCenterData(centerData);
    } catch (error) {
      toast.error(error.message || "Error fetching centers");
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    formik.setFieldValue("packageIds", packageIds);
  }, [packageIds]);

  return (
    <>
      <button
        type="button"
        style={{
          whiteSpace: "nowrap",
        }}
        className="btn btn-button me-2 bt-sm"
        onClick={handleShow}
        disabled={packageIds.length === 0}
      >
        <FaRegCopy /> Copy Packages
      </button>

      <Modal
        show={show}
        size="md"
        aria-labelledby="contained-modal-title-vcenter"
        centered
        onHide={handleClose}
        backdrop={isModified ? "static" : true}
        keyboard={isModified ? false : true}
      >
        <form onSubmit={formik.handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>
              <p className="headColor">Copy Centre Package</p>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="row">
              <div className="col-md-12 col-12 mb-4">
                <label className="form-label">
                  Centre<span className="text-danger">*</span>
                </label>
                <MultiSelect
                  options={centerOptions}
                  value={selectedCenters}
                  onChange={(selected) => {
                    setSelectedCenters(selected);
                    formik.setFieldValue(
                      "centerIds",
                      selected.map((option) => option.value)
                    );
                    setIsModified(true);
                  }}
                  labelledBy="Select Centers"
                  className={`form-multi-select ${
                    formik.touched.centerIds && formik.errors.centerIds
                      ? "is-invalid"
                      : ""
                  }`}
                  style={{
                    height: "37.6px",
                    minHeight: "37.6px",
                  }}
                />
                {formik.touched.centerIds && formik.errors.centerIds && (
                  <div className="invalid-feedback">
                    {formik.errors.centerIds}
                  </div>
                )}
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer className="mt-5">
            <Button
              className="btn btn-sm btn-border bg-light text-dark"
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="btn btn-button btn-sm"
              disabled={loadIndicator}
            >
              {loadIndicator && (
                <span
                  className="spinner-border spinner-border-sm me-2"
                  aria-hidden="true"
                ></span>
              )}
              Submit
            </Button>
          </Modal.Footer>
        </form>
      </Modal>
    </>
  );
}

export default CopyCenterPackages;